import appConfig from '@/config';

const buildSalesEdgeUrl = (url) => `${appConfig.salesEdgeBaseUrl}${url}`;

export default {
  session: {
    callback: '/v0.1/session/callback',
    logout: '/v0.1/logout',
  },
  forgotPassword: {
    post: '/v0.1/forgot-password',
  },
  external: {
    activeStatusPageEvents: '/api/v2/scheduled-maintenances/active.json', // ✅ - not using axios anymore
  },
  publicInviteCode: {
    getOrgId(publicInviteCode) {
      return `/v0.1/public-invite-codes/${publicInviteCode}`;
    },
  },
  sources: {
    all: '/v0.1/sources',
    allV3: '/v0.3/sources',
    filterableSources(sourceId) {
      return `/v0.2/sources/${sourceId}/filterable-sources`;
    },
    allFilterableSources: '/v0.2/sources/filterable-sources',
    mapNewColumns(sourceId) {
      return `/v0.2/sources/${sourceId}/map-new-columns`;
    },
    pair(populationId, partnerPopulationId) {
      return '/v0.1/sources/pair/population'.concat(
        `/${populationId}/partner/${partnerPopulationId}`,
      );
    },
    delete(sourceId) {
      return `/v0.1/sources/${sourceId}`;
    },
    googleSheetsColumnsV2(feedId) {
      return `/v0.2/feeds/${feedId}/google-sheets/details`;
    },
    googleSheetsNames(feedId) {
      return `/v0.1/feeds/${feedId}/google-sheets/sheets`;
    },
  },
  connections: {
    all: '/v0.1/connections',
    snowflake: '/v0.1/connections/snowflake',
    patch(connectionId) {
      return `/v0.1/connections/${connectionId}/settings`;
    },
    populationSettings(connectionId) {
      return `/v0.1/connections/${connectionId}/salesforce/population-settings`;
    },
  },
  feeds: {
    all: '/v0.1/feeds',
    deleteHard(feedId) {
      return `/v0.1/feeds/${feedId}`;
    },
    details(feedId) {
      return `/v0.1/feeds/${feedId}`;
    },
    patch(feedId) {
      return `/v0.1/feeds/${feedId}`;
    },
    sourceFields(feedId) {
      return `/v0.2/feeds/${feedId}/source-fields`;
    },
    sync(feedId) {
      return `/v0.1/feeds/${feedId}/sync`;
    },
    limit: '/v0.1/feeds/limit',
  },
  /* The fact that these are /integrations is a legacy
   * from when we used to call data sources integrations */
  dataSources: {
    all: '/v0.1/integrations',
    requiredColumns(dataSourceType) {
      return `/v0.2/integrations/${dataSourceType}/required-columns`;
    },
  },
  tray: '/v0.1/tray-integrations',
  fileUploads: {
    jsonUploadV3: '/v0.3/file-uploads',
    tables: '/v0.3/file-uploads/tables',
    all: '/v0.2/file-uploads',
    fileOptions: '/v0.1/file-uploads/file-options',
    processFileUpload(uploadId) {
      return `/v0.1/file-uploads/${uploadId}/process-upload`;
    },
  },
  records: {
    v0_2(sourceId, sourcePrimaryKey) {
      return `/v0.2/records/${sourceId}/${sourcePrimaryKey}`;
    },
  },
  auditLogs: {
    export: 'v0.1/audit-log/export',
  },
  matches: {
    export: '/v0.3/matches/export',
  },
  oauth: {
    authorize: '/v0.1/oauth/authorize',
    callback: '/v0.1/oauth/callback',
  },
  overlaps: {
    v0_3: '/v0.3/overlaps',
    byPartner(partnerId) {
      return `/v0.4/overlaps/${partnerId}`;
    },
  },
  proposals: {
    create_v0_4: '/v0.4/proposals',
    accept_v0_3(proposalId) {
      return `/v0.3/proposals/${proposalId}/accept`;
    },
    decline(proposalId) {
      return `/v0.1/proposals/${proposalId}/decline`;
    },
    remove(proposalId) {
      return `/v0.1/proposals/${proposalId}/revoke`;
    },
    byAcceptanceCode(acceptanceCode) {
      return `/v0.1/proposal-codes/${acceptanceCode}/proposal`;
    },
  },
  recordExportUsage: '/v0.1/record-export-usage',
  filterSearch: '/v0.1/search/source-data',
  partners: {
    users(partnerId) {
      return `/v0.1/partners/${partnerId}/users`;
    },
    usersWithListAccess(partnerUUID) {
      return `/v0.1/partners/${partnerUUID}/users?list_access_required=true`;
    },
    overlaps: '/v0.1/partners/global/overlap-counts',
    proposalsReceived: '/v0.1/proposals-received',
    partnersWithFFAccess(ff) {
      return `/v0.1/feature-flags/partners/${ff}`;
    },
    deletePartnership(partnerUUID) {
      return `/v0.1/partners/${partnerUUID}`;
    },
    getVisibility: (partnerOrgUuid) =>
      `/v0.1/partners/${partnerOrgUuid}/team-access`,
  },
  offlinePartners: {
    create: '/v0.1/offline-partners',
    dataSources: {
      tables(uuid) {
        return `/v0.3/file-uploads/tables?offline_partner_org_uuid=${uuid}`;
      },
      sources(uuid) {
        return `/v0.2/sources?offline_partner_org_uuid=${uuid}`;
      },
      filterableSources(sourceId, uuid) {
        return `/v0.2/sources/${sourceId}/filterable-sources?offline_partner_org_uuid=${uuid}`;
      },
      delete(sourceId, uuid) {
        return `/v0.1/sources/${sourceId}?offline_partner_org_uuid=${uuid}`;
      },
      feeds(uuid) {
        return `/v0.2/feeds?offline_partner_org_uuid=${uuid}`;
      },
      presets(uuid) {
        return `/v0.1/data-share-presets?offline_partner_org_uuid=${uuid}`;
      },
    },
    populations: {
      filterSearch(uuid) {
        return `/v0.1/search/source-data?offline_partner_org_uuid=${uuid}`;
      },
    },
  },
  partnerTags: {
    all: '/v0.1/partner-tags',
    put(partnerUuid) {
      return `/v0.1/partners/${partnerUuid}/tags`;
    },
    putPartners(tagId) {
      return `/v0.1/partner-tags/${tagId}`;
    },
    delete(tagId) {
      return `/v0.1/partner-tags/${tagId}`;
    },
    patch(tagId) {
      return `/v0.1/partner-tags/${tagId}`;
    },
  },
  dismissPartnerSuggestions: {
    post: '/v0.1/dismissed-partner-suggestion',
  },
  prospectivePartnerTeam: {
    byInviteCode(publicInviteCode) {
      return `/v0.1/prospective-partner-team/${publicInviteCode}`;
    },
  },
  users: {
    meV2: '/v0.2/users/me',
    suggestedOrgsByEmail: '/v0.1/users/me/suggested-orgs-by-email',
    featureFlags: '/v0.1/feature-flags',
  },
  registration: {
    register: '/v0.1/register',
    domain(domain) {
      const encoded = encodeURIComponent(domain);
      return `/v0.1/registration-domains/${encoded}`;
    },
    cookieEmailDomain: '/v0.1/registration-domains',
  },
  auth0: {
    profiles: '/v0.1/auth0/profiles',
    linkAccounts: '/v0.1/auth0/link',
    signUp: '/v0.1/auth0/sign-up',
  },
  admin: {
    orgs: '/admin/v0.1/organizations',
    org(orgId) {
      return `/admin/v0.1/organizations/${orgId}`;
    },
    users(orgId) {
      return `/admin/v0.1/organizations/${orgId}/users`;
    },
    verify: '/admin/v0.1/verification-changes',
    tempLogin: '/admin/v0.1/temp-login',
  },
  team: {
    deauthorize(userId) {
      return `/v0.1/team/${userId}/deauthorize`;
    },
    list: '/v0.1/team',
    invite: '/v0.2/user-invites',
    change_roles: '/v0.1/team/roles',
  },
  roles: {
    all: '/v0.1/roles',
    create: '/v0.1/roles',
    update(id) {
      return `/v0.1/roles/${id}`;
    },
    delete(roleId) {
      return `/v0.1/roles/${roleId}`;
    },
  },
  all_permissions: '/v0.1/permissions',
  org: {
    patch: '/v0.1/organization',
    login_method: '/v0.1/organization/login-method',
  },
  invites: {
    details(code) {
      return `/v0.1/user-invites/${code}`;
    },
    redeem(code) {
      return `/v0.1/user-invites/${code}/redeem`;
    },
    revoke(id) {
      return `/v0.1/delete-user-invites/${id}`;
    },
  },
  sso: {
    config: '/v0.1/sso/config',
    config_v2: '/v0.2/sso/config',
    auth_connection(id) {
      return `/v0.1/sso/config/${id}/auth-connection`;
    },
    lookup_by_email: '/v0.1/sso/lookup-by-email',
  },
  seatRequests: {
    create: '/v0.1/seat-requests',
    list: '/v0.1/seat-requests',
    update: (uuid) => `/v0.1/seat-requests/${uuid}`,
  },
  inviteRequests: {
    notify(inviteRequestId) {
      return `v0.1/invite-requests/${inviteRequestId}/notify`;
    },
    byId(inviteRequestId) {
      return `v0.1/invite-requests/${inviteRequestId}`;
    },
  },
  partnerIdMappings: {
    get: '/v0.1/partner-id-mapping',
    put: '/v0.1/partner-id-mapping',
  },
  salesEdge: {
    post: {
      messageReadyV2: buildSalesEdgeUrl(
        '/extensions/requests/message-ready/v2?include_unmapped_accounts=true&include_latest_conversation=true',
      ),
    },
    get: {
      partners: (ownOrgUuid) =>
        buildSalesEdgeUrl(`/cb_orgs/${ownOrgUuid}/partners`),
    },
    questions: (orgId) =>
      buildSalesEdgeUrl(`/orgs/${orgId}/bdrequests-questions`),
    accountPartners: (orgId, accountSlug) =>
      buildSalesEdgeUrl(
        `/orgs/${orgId}/accounts/${accountSlug}/account-partners`,
      ),
    parseMessage: (orgId) =>
      buildSalesEdgeUrl(
        `/orgs/${orgId}/bdrequests/parse-message-for-request-workflow`,
      ),
    messages: (orgId) => buildSalesEdgeUrl(`/orgs/${orgId}/bdrequests`),
    records: {
      addToCrm: (crmRecordId) => `/v0.1/contacts/${crmRecordId}/add-to-crm`,
    },
  },
};
